// ─────────────────────────────────────────────────────────────────────────────
// SplashScreen — one-shot opening animation shown the first time the user
// lands on the CrewZ web app in a given browser session. Full-viewport
// black canvas with the CrewZ opening GIF centred, then fades out to
// reveal the app underneath.
//
// Behaviour
//   • Runs every time the app is opened/loaded (user request). Reloading in a
//     new tab / after fully closing the browser plays it again; simple
//     SPA navigations DON'T.
//   • Skipped on the OAuth callback URL (`#session_id=…`) so we never
//     block the auth handshake behind an animation.
//   • Tap / press any key to skip early.
//   • Auto-dismisses ~200 ms after the GIF's runtime (3.9 s) so the user
//     sees the full logo reveal even on slow devices.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';

const SPLASH_DURATION_MS = 4100;  // GIF is 3.9 s + a small buffer
const FADE_MS = 400;

export default function SplashScreen() {
  // Skip entirely if the user has already seen the splash this session,
  // or if we're in the middle of the OAuth callback (hash contains
  // `session_id=`). Never gate auth behind an animation.
  const [visible, setVisible] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      if (window.location.hash?.includes('session_id=')) return false;
    } catch { /* Storage blocked (Safari private mode) — show anyway. */ }
    return true;
  });
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!visible) return undefined;


    const dismiss = () => {
      setFading(true);
      // Unmount after the fade completes so the DOM doesn't linger.
      window.setTimeout(() => setVisible(false), FADE_MS);
    };

    // GIF → fade straight into the app (boat selection)
    const timer = window.setTimeout(dismiss, SPLASH_DURATION_MS);

    // Tap-to-skip. Wrap in try so a rogue listener never blocks the
    // real app from mounting.
    const onSkip = () => { try { dismiss(); } catch { /* noop */ } };
    window.addEventListener('pointerdown', onSkip, { once: true });
    window.addEventListener('keydown', onSkip, { once: true });

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('pointerdown', onSkip);
      window.removeEventListener('keydown', onSkip);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className={`crewz-splash ${fading ? 'fading' : ''}`}
      data-testid="crewz-splash"
      role="dialog"
      aria-label="CREWZ opening animation"
    >
      {/* Bust the browser cache on hard reload so a re-uploaded GIF is
          picked up immediately — the version tag is a build-time
          constant, not a runtime prop. Bumped to v2 for the white
          background variant. */}
      <img
        src="/crewz-opening.gif?v=2"
        alt="CREWZ"
        className="crewz-splash-gif"
        data-testid="crewz-splash-gif"
        draggable={false}
      />
      <button
        type="button"
        className="crewz-splash-skip"
        data-testid="crewz-splash-skip"
        onClick={() => {
          setFading(true);
          window.setTimeout(() => setVisible(false), FADE_MS);
        }}
      >
        Skip
      </button>

      <style>{`
        .crewz-splash {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: #FFFFFF;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 1;
          transition: opacity ${FADE_MS}ms ease-out;
          overscroll-behavior: contain;
        }
        .crewz-splash.fading { opacity: 0; pointer-events: none; }
        .crewz-splash-gif {
          width: min(92vw, 900px);
          height: auto;
          max-height: 92vh;
          user-select: none;
          -webkit-user-drag: none;
        }
        .crewz-splash-gif { transition: opacity 250ms ease-out; }
        .crewz-splash-skip {
          position: absolute;
          bottom: max(env(safe-area-inset-bottom, 0px), 18px);
          right: max(env(safe-area-inset-right, 0px), 18px);
          padding: 8px 16px;
          border-radius: 999px;
          /* Dark chip on the new white background — mirrors the
             inversion from the previous black splash so the Skip
             affordance stays legible without dominating. */
          background: rgba(15, 23, 42, 0.06);
          border: 1px solid rgba(15, 23, 42, 0.14);
          color: rgba(15, 23, 42, 0.75);
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          cursor: pointer;
          transition: background 150ms ease, color 150ms ease;
        }
        .crewz-splash-skip:hover {
          background: rgba(15, 23, 42, 0.12);
          color: #0F172A;
        }
        @media (prefers-reduced-motion: reduce) {
          .crewz-splash { transition: none; }
        }
      `}</style>
    </div>
  );
}
